import { Language } from '../models/serializable/language.js';
import { LocalizedString } from '../models/serializable/localized-string.js';
import { Seat } from '../models/serializable/seating/seat.js';
import { SeatRow, SeatRowNamingStrategy } from '../models/serializable/seating/seat-row.js';
import { SeatingElement } from '../models/serializable/seating/seating-element.js';
import { Sector } from '../models/serializable/seating/sector.js';
import { assertUnreachable } from '../utility/general-utility.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../utility/null-safety.js';

// Moved from in-time-web
// TODO(emil): refactor if this needs localization.
const MIDDLE_SEAT_LABEL = 'K';

export class SeatNameFactory {
  static findLocalizedNameForSeat(
      seat: Seat,
      language: Language,
  ): string | null {
    const sectorPath = this.joinParentSeatingElementsNames(
        seat,
        seat,
        language,
    );

    const seatName = this.findSeatProperName(seat, language);
    return sectorPath != null ? `${sectorPath}, ${seatName}` : seatName;
  }

  static generateLocalizedStringForSeat(
      seat: Seat,
  ): LocalizedString {
    const map = new Map<Language, string>();
    for(const language of Object.values(Language)) {
      const name = this.findLocalizedNameForSeat(seat, language);
      if(isNotNullOrUndefined(name)) {
        map.set(language, name);
      }
    }

    return new LocalizedString(map);
  }

  private static joinParentSeatingElementsNames(
      sourceOrParent: SeatingElement,
      child: SeatingElement,
      language: Language,
  ): string | null {
    if(isNullOrUndefined(sourceOrParent.parent)) {
      return null;
    }

    const element = sourceOrParent.parent.deref();
    if(isNullOrUndefined(element)) {
      return null;
    }

    if(element instanceof Sector) {
      const sectorName = this.findSectorOrRowName(element, language);
      const parentName = this.joinParentSeatingElementsNames(
          element,
          child,
          language,
      );

      if(isNullOrUndefined(parentName)) {
        return sectorName;
      }
      else if(isNullOrUndefined(sectorName)) {
        return parentName;
      }

      return `${parentName}, ${sectorName}`;
    }
    else if(element instanceof SeatRow) {
      const sectorName = this.findRowName(
          element,
          child,
          language,
      );

      const parentName = this.joinParentSeatingElementsNames(
          element,
          child,
          language,
      );

      if(isNullOrUndefined(parentName)) {
        return sectorName;
      }
      else if(isNullOrUndefined(sectorName)) {
        return parentName;
      }

      if(element.namingStrategy === SeatRowNamingStrategy.LeftRight) {
        return `${parentName} - ${sectorName}`;
      }
      else if(element.namingStrategy === SeatRowNamingStrategy.LeftRightMiddle) {
        return `${parentName} - ${sectorName}`;
      }
      else {
        return `${parentName}, ${sectorName}`;
      }
    }

    return null;
  }

  private static findSectorOrRowName(
      element: Sector | SeatRow,
      language: Language,
  ): string | null {
    const texts = element.label?.texts ?? null;
    if(isNullOrUndefined(texts)) {
      return null;
    }

    return this.findLocalizedText(texts, language);
  }

  private static findRowName(
      element: SeatRow,
      child: SeatingElement,
      language: Language,
  ): string | null {
    const strategy = element.namingStrategy;
    switch(strategy) {
      case SeatRowNamingStrategy.Sequential:
        return this.findSectorOrRowName(element, language);
      case SeatRowNamingStrategy.LeftRight:
        return this.findLeftRightRowName(element, child, language);
      case SeatRowNamingStrategy.LeftRightMiddle:
        return this.findLeftRightMiddleRowName(element, child, language);
      default:
        assertUnreachable(strategy);
    }
  }

  private static findLeftRightRowName(
      element: SeatRow,
      child: SeatingElement,
      language: Language,
  ): string | null {
    let index: number = -1;
    for(let i = 0; i < element.seats.length && index < 0; i++) {
      if(element.seats[i].uniqueId === child.uniqueId) {
        index = i;
      }
    }

    const direction = index < element.seats.length / 2 ?
      this.getLocalizedLeft(language) :
      this.getLocalizedRight(language);

    const texts = element.label?.texts ?? null;
    if(isNullOrUndefined(texts)) {
      return direction;
    }

    const localizedText = this.findLocalizedText(texts, language);
    if(isNullOrUndefined(localizedText)) {
      return direction;
    }

    return `${direction}, ${localizedText}`;
  }

  private static findLeftRightMiddleRowName(
      element: SeatRow,
      child: SeatingElement,
      language: Language,
  ): string | null {
    let index: number = -1;
    for(let i = 0; i < element.seats.length && index < 0; i++) {
      if(element.seats[i].uniqueId === child.uniqueId) {
        index = i;
      }
    }

    const length = element.seats.length;
    const middle = Math.floor(length / 2);

    let direction: string;
    if(index === middle) {
      direction = this.getLocalizedMiddle(language);
    }
    else {
      direction = index < element.seats.length / 2 ?
        this.getLocalizedLeft(language) :
        this.getLocalizedRight(language);
    }

    const texts = element.label?.texts ?? null;
    if(isNullOrUndefined(texts)) {
      return direction;
    }

    const localizedText = this.findLocalizedText(texts, language);
    if(isNullOrUndefined(localizedText)) {
      return direction;
    }

    return `${direction}, ${localizedText}`;
  }

  private static findLocalizedText(
      texts: Map<Language, string | null>,
      language: Language,
  ): string | null {
    const languageMatch = texts.get(language);
    if(isNotNullOrUndefined(languageMatch)) {
      return languageMatch;
    }

    const englishMatch = texts.get(Language.English);
    if(isNotNullOrUndefined(englishMatch)) {
      return englishMatch;
    }

    for(const [, text] of texts) {
      if(isNotNullOrUndefined(text)) {
        return text;
      }
    }

    return null;
  }

  private static getLocalizedLeft(language: Language): string {
    switch(language) {
      case Language.English:
        return 'Left';
      case Language.Romanian:
        return 'Stânga';
      case Language.Hungarian:
        return 'Bal';
      default:
        assertUnreachable(language);
    }
  }

  private static getLocalizedRight(language: Language): string {
    switch(language) {
      case Language.English:
        return 'Right';
      case Language.Romanian:
        return 'Dreapta';
      case Language.Hungarian:
        return 'Jobb';
      default:
        assertUnreachable(language);
    }
  }

  private static getLocalizedSeat(language: Language): string {
    switch(language) {
      case Language.English:
        return 'Seat';
      case Language.Romanian:
        return 'Loc';
      case Language.Hungarian:
        return 'Szék';
      default:
        assertUnreachable(language);
    }
  }

  private static findSeatProperName(seat: Seat, language: Language): string | null {
    const parent = seat.parent?.deref();
    if(isNotNullOrUndefined(parent)) {
      if(parent instanceof SeatRow) {
        if(parent.namingStrategy === SeatRowNamingStrategy.Sequential) {
          const index = parent.seats.indexOf(seat);
          return index >= 0 ? `${this.getSeatInRightOrder(index + parent.firstLabelingOrdinal, language)}` : null;
        }
        else if(parent.namingStrategy === SeatRowNamingStrategy.LeftRight) {
          const length = parent.seats.length;
          const halfLength = length / 2;
          const index = parent.seats.indexOf(seat);
          if(index < halfLength) {
            return `${this.getSeatInRightOrder(index + parent.firstLabelingOrdinal, language)}`;
          }
          else {
            return `${this.getSeatInRightOrder(length - 1 - index + parent.firstLabelingOrdinal, language)}`;
          }
        }
        else if(parent.namingStrategy === SeatRowNamingStrategy.LeftRightMiddle) {
          const length = parent.seats.length;
          const middle = Math.floor(length / 2);
          const index = parent.seats.indexOf(seat);
          if(index === middle) {
            return this.getLocalizedMiddleSeat(language);
          }
          else if(index < middle) {
            return `${this.getSeatInRightOrder(index + parent.firstLabelingOrdinal, language)}`;
          }
          else {
            return `${this.getSeatInRightOrder(length - 1 - index + parent.firstLabelingOrdinal, language)}`;
          }
        }
      }
    }

    return null;
  }

  private static getSeatInRightOrder(index: number, language: Language): string {
    switch(language) {
      case Language.English:
        return `${this.getLocalizedSeat(language)} ${index}`;
      case Language.Romanian:
        return `${this.getLocalizedSeat(language)} ${index}`;
      case Language.Hungarian:
        return `${index}. ${this.getLocalizedSeat(language)}`;
      default:
        assertUnreachable(language);
    }
  }

  private static getLocalizedMiddle(language: Language): string {
    switch(language) {
      case Language.English:
        return 'Middle';
      case Language.Romanian:
        return 'Mijloc';
      case Language.Hungarian:
        return 'Közép';
      default:
        assertUnreachable(language);
    }
  }

  private static getLocalizedMiddleSeat(language: Language): string {
    switch(language) {
      case Language.Hungarian:
        return `${MIDDLE_SEAT_LABEL} ${this.getLocalizedSeat(language)}`;
      case Language.English:
        return `${this.getLocalizedSeat(language)} ${MIDDLE_SEAT_LABEL}`;
      case Language.Romanian:
        return `${this.getLocalizedSeat(language)} ${MIDDLE_SEAT_LABEL}`;
      default:
        assertUnreachable(language);
    }
  }
}
